import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Mail, MapPin, Phone } from 'lucide-react';
import { mockData } from '../mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                DL
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg leading-tight">Dr. Lim Siow Jin</span>
                <span className="text-xs text-emerald-300 leading-tight">Dhyanacharya Nagajiva</span>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Visionary entrepreneur, Zen master, and wellness pioneer. Integrating business excellence, natural healing, and spiritual wisdom for global transformation.
            </p>
            <div className="flex space-x-3">
              <a
                href="https://instagram.com/limsiowjin"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-emerald-600 transition-all hover:scale-110"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://youtube.com/@sunyastudios"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-red-600 transition-all hover:scale-110"
              >
                <Youtube size={18} />
              </a>
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-blue-600 transition-all hover:scale-110"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-emerald-300">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/about" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  About Dr. Lim
                </Link>
              </li>
              <li>
                <Link to="/talks" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  My Talks
                </Link>
              </li>
              <li>
                <Link to="/articles" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Articles
                </Link>
              </li>
              <li>
                <Link to="/dxn" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  DXN Legacy
                </Link>
              </li>
              <li>
                <Link to="/sunya" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Sunya Meditation
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Resources
                </Link>
              </li>
            </ul>
          </div>

          {/* Explore */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-emerald-300">Explore</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/sunya/retreats" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Sunya Retreats
                </Link>
              </li>
              <li>
                <Link to="/sunya/online" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Online Training
                </Link>
              </li>
              <li>
                <Link to="/wellness/ganotherapy" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Ganotherapy
                </Link>
              </li>
              <li>
                <Link to="/wellness/tcm" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Traditional Chinese Medicine
                </Link>
              </li>
              <li>
                <Link to="/peace" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Peace & Humanitarian
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-emerald-300">Connect</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-emerald-400 mt-1 flex-shrink-0" />
                <span className="text-gray-300 text-sm">
                  DXN Cyberville<br />
                  Cyberjaya, Selangor<br />
                  Malaysia
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone size={18} className="text-emerald-400 flex-shrink-0" />
                <span className="text-gray-300 text-sm">+603-8680 9861</span>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={18} className="text-emerald-400 flex-shrink-0" />
                <a href="mailto:contact@drlimsiowjin.com" className="text-gray-300 hover:text-emerald-300 transition-colors text-sm">
                  contact@drlimsiowjin.com
                </a>
              </li>
            </ul>

            {/* Newsletter */}
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-2 text-emerald-300">Newsletter</h4>
              <div className="flex space-x-2">
                <input
                  type="email"
                  placeholder="Your email"
                  className="flex-1 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <button className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors text-sm font-medium">
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © {currentYear} Dr. Lim Siow Jin. All rights reserved.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-400 hover:text-emerald-300 transition-colors text-sm">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-gray-400 hover:text-emerald-300 transition-colors text-sm">
                Terms of Service
              </Link>
              <Link to="/sitemap" className="text-gray-400 hover:text-emerald-300 transition-colors text-sm">
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;